import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, Hidden, IconButton,
    InputAdornment,
    TextField,
    Typography, useTheme
} from "@material-ui/core";
import {Add, ExpandMore, MoreVert} from "@material-ui/icons";
import Rest from "../../../../helpers/Rest";
import QRCodeScanner from "../../components/QRCodeScanner";
import {Autocomplete} from "@material-ui/lab";
import {getDeviceType} from "../../Config";
import QRCodeIcon from "../QRCodeIcon";
import FullScreenPopup from "../../components/FullScreenPopup";
import TestResultIcon from "../TestResultIcon";
import {makeStyles} from "@material-ui/styles";
import clsx from "clsx";

function path(deviceId, path){
    return `device/${deviceId}/component${path ? '/' + path : ''}`
}

const FIELDS = ["component_type", "title", "notes", "id"];
const UNTESTED = 0, PASSED = 1, FAILED = 2

const useStyle = makeStyles(theme => {
    return {
        root: {
            height: 53,
            textTransform: "none",
            fontWeight: 400,
            "& svg":{
                height: 24
            },
            "& .MuiButton-label": {
                display: "flex",
                justifyContent: "space-between",
                padding: "6px 16px"
            }
        }
    }
})

function AddButton({children, onClick, onOpenScanner}){
    const style = useStyle(useTheme())
    return <Button
        variant="outlined"
        size="large"
        endIcon={window.BarcodeDetector && onOpenScanner ? <QRCodeIcon style={{height:24}} onClick={e => {
            e.stopPropagation()
            onOpenScanner()
        }}/> : null}
        onClick={onClick}
        className={clsx(style.root, "MuiFormLabel-root")}
        fullWidth
    >
        {children}
    </Button>
}

class ComponentEditor extends React.Component{
    constructor(props) {
        super(props);
        this.state = Object.assign({
            open: !props.component.id,
            testState: UNTESTED
        }, props.component)

        this.update = e => this.setState({
            [e.target.name]: e.target.value,
            testState: UNTESTED
        })

        this.setType = (_, value) => this.setState({
            component_type: value,
            testState: UNTESTED
        })

        this.open = () => this.setState({
            open: true
        })

        this.cancel = () => this.props.component.id ? this.setState({
            open: false,
            ...props.component
        }) : this.delete()

        this.delete = () => this.props.onDelete(props.component)

        this.save = () => {
            this.setState({
                open: false
            })
            props.onSave(this.props.component, FIELDS.reduce((map, key) => {
                const val = this.state[key]
                if (val !== undefined){
                    map[key] = val
                }
                return map
            }, {}))
        }

        this.setUpScanning()
    }

    setPassed(passed){
        this.setState({
            testState: passed ? PASSED : FAILED,
            scanning: false
        })
        this.props.onTestCompleted(this.props.component, passed)
    }

    setUpScanning(){
        this.toggleScan = () => this.setState({
            scanning: !this.state.scanning
        })

        this.gotQrCode = result => {
            Rest.translateDeviceUrl(result).then(reply => {
                this.setPassed(reply.id === this.state.title)
            }).catch(() => {
                this.setPassed(result === this.state.title)
            })
        }

        this.gotError = error => {
            console.log(error)
            this.toggleScan()
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {component} = this.props
        if (component !== prevProps.component){
            this.setState({
                open: !component.id,
                ...component
            })
        }
    }

    textField(){
        const {component_type, title, id} = this.props.component
        return <TextField
            onChange={this.action}
            variant="outlined"
            onClick={this.open}
            label={component_type}
            value={title}
            InputProps={{
                endAdornment: <InputAdornment position="end">
                    <MoreVert/>
                </InputAdornment>
            }}
            {...(this.props.testable && id ? {
                style: {
                    flexGrow: 1
                }
            } : {
                fullWidth: true
            })}
        />
    }

    icon(){
        switch (this.state.testState){
            case UNTESTED:
                return <IconButton onClick={this.toggleScan}>
                    <QRCodeIcon style={{width:24, height:24}}/>
                </IconButton>
            case PASSED:
                return <TestResultIcon passedTest/>
            default:
                return <TestResultIcon onClick={this.toggleScan}/>
        }
    }

    test(){
        return <>
            {this.textField()}
            {this.icon()}
            {this.state.scanning ? <QRCodeScanner
                onResult={this.gotQrCode}
                onError={this.gotError}
                onClose={this.toggleScan}
            /> : null}
        </>
    }

    dialogFields(){
        const {notes, component_type, title} = this.state
        return <>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    freeSolo
                    value={component_type || ''}
                    onChange={this.setType}
                    onInputChange={this.setType}
                    renderInput={params => <TextField
                        {...params}
                        variant="outlined"
                        name="component_type"
                        label={this.props.typeLabel}
                        fullWidth
                    />}
                    options={this.props.typeOptions || []}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="title"
                    value={title}
                    onChange={this.update}
                    variant="outlined"
                    label="Title"
                    fullWidth
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="notes"
                    value={notes}
                    onChange={this.update}
                    variant="outlined"
                    label="Notes"
                    rows={4}
                    fullWidth
                    multiline
                />
            </Grid>
        </>
    }

    dialogButtons(){
        return <>
            <Button
                variant="text"
                onClick={this.cancel}
            >Cancel</Button>
            {this.state.id ? <Button
                variant="text"
                onClick={this.delete}
            >Delete</Button> : null}
            <Button
                variant="contained"
                color="primary"
                onClick={this.save}
            >Save</Button>
        </>
    }

    dialog(){
        const {open, id} = this.state
        return <Dialog open={open} maxWidth="sm" onClose={this.cancel}>
            <DialogTitle>{id ? "Edit" : "Add"} {this.props.label}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {this.dialogFields()}
                </Grid>
            </DialogContent>
            <DialogActions>
                {this.dialogButtons()}
            </DialogActions>
        </Dialog>
    }

    modal(){
        const {open, id} = this.state
        return <FullScreenPopup open={open}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h6">{id ? "Edit" : "Add"} Component</Typography>
                </Grid>
                {this.dialogFields()}
                <Grid item xs={12} container justify="flex-end">
                    {this.dialogButtons()}
                </Grid>
            </Grid>
        </FullScreenPopup>
    }

    render() {
        return <>
            {this.props.testable ? this.test() : this.textField()}
            <Hidden smUp>
                {this.modal()}
            </Hidden>
            <Hidden xsDown>
                {this.dialog()}
            </Hidden>
        </>
    }
}

export function fetchComponents(deviceId){
    return Rest.get(path(deviceId, 'list'))
}

export function mapCompletedTests(components, results){
    return components.map(component => {
        return {
            title: component.title,
            component_type: component.component_type,
            passed: results[component.id] || false
        }
    })
}

export class Components extends React.Component{
    constructor(props) {
        super(props);
        const completedTests = {}
        this.state = {
            add: '',
            components: props.components,
            scanning: false,
            open: true
        }

        this.onTestCompleted = (component, passed) => {
            completedTests[component.id] = passed
            this.props.onTestCompleted(
                mapCompletedTests(this.state.components, completedTests)
            )
        }

        this.modified = components => {
            this.props.onModified(components || this.state.components)
        }

        this.toggle = () => this.setState({
            open: !this.state.open
        })

        this.delete = (component) => {
            this.setState({
                components: this.state.components.filter(item => item !== component)
            })
            if (component.id){
                Rest.del(path(props.deviceId, component.id)).then(this.modified)
            }
        }

        this.save = (component, newValues) => {
            if (component.id) {
                const comp = this.updateItem(component, newValues)
                this.setState({
                    components: comp
                })
                Rest.put(path(props.deviceId, component.id), newValues).then(() => this.modified(comp))
            }
            else{
                Rest.post(path(props.deviceId), newValues).then(reply => {
                    const comp = this.updateItem(component, reply)
                    this.modified(comp)
                    this.setState({
                        components: comp
                    })
                })
            }
        }

        this.addManually = () => {
            const comp = this.state.components.concat([{
                component_type: props.allocateType ? props.allocateType(this.state.components.length) : "",
                title: "",
                notes: ""
            }])
            this.setState({
                components: comp
            })
        }

        this.startScanning = () => this.setState({
            scanning: true
        })

        this.stopScanning = () => this.setState({
            scanning: false
        })

        this.gotQrCode = result => {
            const {allocateType} = this.props
            this.stopScanning()
            Rest.translateDeviceUrl(result).then(reply => {
                if (allocateType){
                    this.add(allocateType(this.state.components.length), reply.id)
                }
                else{
                    const type = getDeviceType(reply.id)
                    if (type){
                        this.add(type.value, reply.id)
                    }
                    else{
                        this.add('', reply.id)
                    }
                }
            }).catch(() => {
                this.add(allocateType ? allocateType(this.state.components.length) : '', result)
            })
        }

        this.gotError = error => {
            console.log(error)
            this.stopScanning()
        }
    }

    updateItem(component, newValues){
        return this.state.components.map(
            item => item === component ? newValues : item
        )
    }

    add(type, title){
        this.setState({
            components: this.state.components.concat([{
                component_type: type,
                title: title,
                notes: ""
            }])
        })
    }

    scanner(){
        return <QRCodeScanner
            onResult={this.gotQrCode}
            onError={this.gotError}
            onClose={this.stopScanning}
        />
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {components} = this.props
        if (components !== prevProps.components && components !== this.state.components){
            this.completedTests = {}
            this.setState({
                components: components
            })
        }
    }

    accordion(components){
        const {title, typeOptions, testable, typeLabel, children, label, className} = this.props
        /*return <Accordion elevation={0} expanded={this.state.open}>
            <AccordionSummary
                expandIcon={<ExpandMore />}
                onClick={this.toggle}
            >
                <Typography variant="h3">{title}<span>({components.length})</span></Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    {components.sort(
                        (a, b) => a.component_type.localeCompare(b.component_type)
                    ).map((component) => <Grid item xs={12} container alignItems="center" key={component.id || 'new'}>
                        <ComponentEditor
                            component={component}
                            onSave={this.save}
                            onDelete={this.delete}
                            open={!component.id}
                            typeOptions={typeOptions}
                            testable={testable}
                            onTestCompleted={this.onTestCompleted}
                            typeLabel={typeLabel}
                            label={label}
                        />
                    </Grid>)}
                    <Grid item container xs={12} justify="flex-end">
                        {children}
                        <AddButton
                            onClick={window.BarcodeDetector ? this.startScanning : this.addManually}
                        >
                            {`Add ${label}`}
                        </AddButton>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>*/
        return <Grid container spacing={2} className={className}>
            {components.sort(
                (a, b) => a.component_type.localeCompare(b.component_type)
            ).map((component) => <Grid item xs={12} container alignItems="center" key={component.id || 'new'}>
                <ComponentEditor
                    component={component}
                    onSave={this.save}
                    onDelete={this.delete}
                    open={!component.id}
                    typeOptions={typeOptions}
                    testable={testable}
                    onTestCompleted={this.onTestCompleted}
                    typeLabel={typeLabel}
                    label={label}
                />
            </Grid>)}
            <Grid item container xs={12} justify="flex-end">
                {children}
                <AddButton
                    onClick={this.addManually}
                    onOpenScanner={this.startScanning}
                >
                    {`Add ${label}`}
                </AddButton>
            </Grid>
        </Grid>
    }

    render() {
        const {components, scanning} = this.state;
        return scanning ? this.scanner() : this.accordion(components)
    }
}
